import { useCallback } from 'react';

import './TagListSelect.scss';

import type { SelectProps } from 'antd';

import OptionCheckIcon from 'src/assets/option-check.svg?react';
import { cNs } from 'src/helpers';
import type { IControl, ISelectOption } from 'src/types';

type TagListSelectOption = string | null | undefined;
export type TagListSelectValue = TagListSelectOption | TagListSelectOption[];

export type TagListSelectProps = IControl<TagListSelectValue> &
  Pick<SelectProps, 'status'> & {
    optionsClassName?: string;
    optionClass?: string | ((option: ISelectOption<TagListSelectOption>) => string);
    options?: ISelectOption<TagListSelectOption>[];

    value?: TagListSelectValue;
    multiselect?: boolean;
    disabled?: boolean;
    description?: string | null;
  };

export function TagListSelect({
  label,
  multiselect,
  disabled,
  value,
  controlStyle,
  controlClassName,
  description,
  optionsClassName,
  optionClass,
  options,
  status,
  onChange,
}: TagListSelectProps) {
  const handleSelect = useCallback(
    (option: ISelectOption<TagListSelectOption>, newSelected: boolean) => {
      if (multiselect) {
        const currentValues = Array.isArray(value) ? value : [value];

        onChange?.(
          newSelected
            ? currentValues.includes(option.value)
              ? currentValues
              : [...currentValues, option.value]
            : currentValues.filter((val) => val !== option.value)
        );
      } else {
        onChange?.(newSelected ? option.value : option.value === value ? null : value);
      }
    },
    [multiselect, value, onChange]
  );

  return (
    <div className={cNs('tag-list-select__container', controlClassName)} style={controlStyle}>
      <div className="flex column nowrap">
        {label ? typeof label !== 'object' ? <div className="tag-list-select__title">{label}</div> : label : null}
        {description ? <div className="tag-list-select__description">{description}</div> : null}
      </div>
      <div className={cNs('tag-list-select__options', optionsClassName)}>
        {options?.map((option) => {
          const isSelected = Array.isArray(value) ? value.includes(option.value) : value === option.value;

          return (
            <div
              key={option.value}
              className={cNs(
                'tag-list-select__option',
                isSelected && 'tag-list-select__option-selected',
                (disabled || option.disabled) && 'tag-list-select__option-disabled',
                status === 'warning'
                  ? 'tag-list-select__option-warning'
                  : status === 'error'
                    ? 'tag-list-select__option-error'
                    : null,
                typeof optionClass === 'function' ? optionClass(option) : optionClass
              )}
              onClick={disabled || option.disabled ? undefined : () => handleSelect(option, !isSelected)}
            >
              {isSelected && <OptionCheckIcon className="tag-list-select__option-check" />}
              <span>{option.label}</span>
            </div>
          );
        }) ?? null}
      </div>
    </div>
  );
}
